import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { bdcApi } from "../api/bdc";
import { notificationsApi } from "../api/notifications";

const BASE_TITLE = "Lumika";

/**
 * Ambient unread badges (UIUX §5.5 / §8.3): the tab title becomes
 * "(3) Lumika" and the installed-PWA icon gets a numeric badge
 * (`navigator.setAppBadge`), so a rep in another tab sees the number
 * move without waiting on the bell poll.
 *
 * Count = unread notifications + needs-you queue size (B3's real
 * counts endpoint, shared query keys with the bell and Home — no new
 * traffic). The title badge clears while the tab is focused (you're
 * looking at it); the app badge tracks the raw count.
 *
 * Returns the needs-you count so the bottom-nav Inbox item can render
 * the same number (§8.1).
 */
export function useAppBadge(): { needsYou: number } {
  const notifQuery = useQuery({
    queryKey: ["notifications", "count"],
    queryFn: () => notificationsApi.count(),
    refetchInterval: 60_000,
  });
  const countsQuery = useQuery({
    queryKey: ["bdc", "inbox", "counts", "ambient", true],
    queryFn: () => bdcApi.inboxCounts({ includeAnonymous: true }),
  });

  const needsYou = countsQuery.data?.["needs_you"] ?? 0;
  const total = (notifQuery.data?.unread ?? 0) + needsYou;

  const [focused, setFocused] = useState(
    typeof document !== "undefined" ? document.visibilityState === "visible" : true,
  );
  useEffect(() => {
    const onVisibility = () => setFocused(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  useEffect(() => {
    document.title = !focused && total > 0 ? `(${total}) ${BASE_TITLE}` : BASE_TITLE;
    return () => {
      document.title = BASE_TITLE;
    };
  }, [focused, total]);

  useEffect(() => {
    const nav = navigator as Navigator & {
      setAppBadge?: (count: number) => Promise<void>;
      clearAppBadge?: () => Promise<void>;
    };
    if (total > 0) void nav.setAppBadge?.(total)?.catch(() => undefined);
    else void nav.clearAppBadge?.()?.catch(() => undefined);
  }, [total]);

  return { needsYou };
}
